import type { Currency } from '@/types'

// Exchange rates relative to EUR
// These should ideally come from an API in production
export const EXCHANGE_RATES: Record<Currency, number> = {
  EUR: 1,        // Base currency
  USD: 1.10,     // 1 EUR = 1.10 USD
  HUF: 390,      // 1 EUR = 390 HUF (approximate)
  GBP: 0.86,     // 1 EUR = 0.86 GBP
  CHF: 0.95      // 1 EUR = 0.95 CHF
}

/**
 * Convert amount from one currency to another
 * @param amount Amount in source currency
 * @param fromCurrency Source currency
 * @param toCurrency Target currency
 * @returns Converted amount in target currency
 */
export function convertCurrency(
  amount: number,
  fromCurrency: Currency,
  toCurrency: Currency
): number {
  if (fromCurrency === toCurrency) {
    return amount
  }
  
  // Convert to EUR first (base currency)
  const amountInEUR = amount / EXCHANGE_RATES[fromCurrency]
  
  // Then convert to target currency
  return amountInEUR * EXCHANGE_RATES[toCurrency]
}

/**
 * Format currency with proper symbol and decimals
 * Note: This formats the amount as-is without conversion
 * Use convertCurrency first if you need to convert between currencies
 */
export function formatCurrency(amount: number, currency: Currency): string {
  const symbols: Record<Currency, string> = {
    EUR: '€',
    USD: '$',
    HUF: 'Ft',
    GBP: '£',
    CHF: 'CHF'
  }
  
  const symbol = symbols[currency] || currency
  const formatted = amount.toFixed(2)
  
  // HUF typically doesn't use decimals and goes after the amount
  if (currency === 'HUF') {
    return `${Math.round(amount)} ${symbol}`
  }
  
  return `${symbol}${formatted}`
}

/**
 * Convert and format currency in one step
 * @param amount Amount in EUR (base currency)
 * @param targetCurrency Target currency to display
 * @returns Formatted string with converted amount
 */
export function formatCurrencyFromEUR(amount: number, targetCurrency: Currency): string {
  const converted = convertCurrency(amount, 'EUR', targetCurrency)
  return formatCurrency(converted, targetCurrency)
}
